import { PropsWithChildren, useLayoutEffect, useRef } from 'react';
import { animate } from 'motion';

import { DataListContext } from '@components/patterns/DataList/context';
import { DataListGroup } from '@components/patterns/DataList/group';
import { DataListItem } from '@components/patterns/DataList/item';
import { cn, easeOutSmooth } from '@components/primitives/utilities';

import css from './styles.module.css';

export interface DataListProps extends PropsWithChildren {
  className?: string;
  size?: 'sm' | 'md';
}

function Root({ children, className, size = 'md' }: DataListProps) {
  const ref = useRef<HTMLDListElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    function update() {
      if (!element) return;

      const left = element.getBoundingClientRect().x;
      const labels = element.querySelectorAll(`.${css.labelText}`);
      const widths = Array.from(labels).map((label) => {
        if (!(label instanceof HTMLElement)) return 0;
        if (label.matches(`.${css.groupRoot}[data-state='closed'] &`)) {
          return 0;
        }
        const offsetLeft = label.getBoundingClientRect().x - left;
        return label.offsetWidth + offsetLeft;
      });
      const maxWidth = Math.max(...widths);

      const current = element.style.getPropertyValue('--max-label-width');
      if (!current || current === `${maxWidth}px`) {
        element.style.setProperty('--max-label-width', `${maxWidth}px`);
      } else {
        animate(
          element,
          { '--max-label-width': `${maxWidth}px` },
          {
            duration: 0.25,
            ease: easeOutSmooth,
          }
        );
      }
    }

    update();

    const mutations = new MutationObserver(update);
    mutations.observe(element, {
      childList: true,
      subtree: true,
      attributeFilter: ['data-state'],
      attributes: true,
    });

    const resize = new ResizeObserver(update);
    resize.observe(element);

    return () => {
      mutations.disconnect();
      resize.disconnect();
    };
  }, []);

  return (
    <DataListContext.Provider value={{ groups: [] }}>
      <dl ref={ref} className={cn(css.root, className)} data-size={size}>
        {children}
      </dl>
    </DataListContext.Provider>
  );
}

export const DataList = Object.assign(Root, {
  Item: DataListItem,
  Group: DataListGroup,
});

export type { DataListGroupProps } from './group';
export type { DataListItemProps } from './item';
export * from './types';
